import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  IFeedMessage,
  IFlowLog,
  IFlowStepLogData,
  IFlowStepParam,
  ScrapedSites,
  TChatFlowType,
  TTaskStatus,
} from '@/types/chat';

export type TState = {
  feed: IFeedMessage[];
  lastFeedMessage: IFeedMessage | null;
  showChatModal: boolean;
  chatFlowId: TChatFlowType;
  chatFlowStep: IFlowStepParam | null;
  chatFlowLog: IFlowLog;
  waitingForAnswer: boolean;
  waitingForInky: boolean;
  stateId: string;
  chatIsDone: boolean;
  activeTasks: ScrapedSites[];
  isFrontendFlowComplete: boolean;
  isBackendFlowActive: boolean;
  projectId: string | null;
  socialUrls: Record<string, string>;
  websiteUrls: string[];
  hasError: boolean;
  isBrandStrategyCreating: boolean;
  brandStrategyTaskIds: string[];
};

const initialState: TState = {
  feed: [],
  lastFeedMessage: null,
  showChatModal: false,
  chatFlowId: 'newClient',
  chatFlowStep: null,
  chatFlowLog: { steps: {} },
  waitingForAnswer: false,
  waitingForInky: false,
  stateId: '',
  chatIsDone: false,
  activeTasks: [],
  isFrontendFlowComplete: false,
  isBackendFlowActive: false,
  projectId: null,
  socialUrls: {},
  websiteUrls: [''],
  hasError: false,
  isBrandStrategyCreating: false,
  brandStrategyTaskIds: [],
};

type TActions = {
  addFeedMessage: (
    feedMessage: IFeedMessage,
    timeout?: number
  ) => Promise<IFeedMessage>;
  patchFeedMessage: (id: string, update: Partial<IFeedMessage>) => void;
  resetChat: () => void;
  setShowChatModal: (showChatModal: boolean) => void;
  setModalFlow: (chatFlowId: TChatFlowType) => void;
  setChatFlowStep: (chatFlowStep: IFlowStepParam) => void;
  setChatFlowLog: (chatFlowStepId: number, stepData: IFlowStepLogData) => void;
  setWaitingForAnswer: (waitingForAnswer: boolean) => void;
  setWaitingForInky: (waitingForInky: boolean) => void;
  setStateId: (stateId: string) => void;
  setChatIsDone: (chatIsDone: boolean) => void;
  addTask: (task: ScrapedSites) => void;
  updateTaskStatus: (
    taskId: string,
    status: TTaskStatus,
    error?: string
  ) => void;
  setFrontendFlowComplete: (isComplete: boolean) => void;
  setBackendFlowActive: (isActive: boolean) => void;
  clearTasks: () => void;
  setProjectId: (projectId: string) => void;
  setSocialUrls: (urls: Record<string, string>) => void;
  updateSocialUrl: (socialType: string, url: string) => void;
  setWebsiteUrl: (url: string) => void;
  updateWebsiteUrl: (index: number, url: string) => void;
  setHasError: (hasError: boolean) => void;
  setIsBrandStrategyCreating: (isCreating: boolean) => void;
  setBrandStrategyTaskIds: (taskIds: string[]) => void;
};

type TStore = TState & TActions;

const useChatStore = create(
  persist(
    immer<TStore>((set, get) => ({
      ...initialState,

      resetChat: () => {
        set((state) => {
          const projectId = state.projectId;
          state.feed = [];
          state.stateId = '';
          state.lastFeedMessage = null;
          state.chatFlowStep = null;
          state.chatFlowLog = { steps: {} };
          state.activeTasks = [];
          state.isFrontendFlowComplete = false;
          state.isBackendFlowActive = false;
          state.chatIsDone = false;
          state.projectId = projectId;
          state.socialUrls = {};
        });
      },

      setProjectId: (projectId) => {
        set((state) => {
          if (state.projectId !== projectId) {
            state.feed = [];
            state.lastFeedMessage = null;
            state.chatFlowStep = null;
            state.chatFlowLog = { steps: {} };
            state.waitingForAnswer = false;
            state.waitingForInky = false;
            state.stateId = '';
            state.chatIsDone = false;
            state.activeTasks = [];
            state.isFrontendFlowComplete = false;
            state.isBackendFlowActive = false;
            state.socialUrls = {};
          }
          state.projectId = projectId;
        });
      },

      setSocialUrls: (urls) => {
        set((state) => {
          state.socialUrls = urls;
        });
      },

      updateSocialUrl: (socialType, url) => {
        set((state) => {
          state.socialUrls = {
            ...state.socialUrls,
            [socialType]: url,
          };
        });
      },

      setWebsiteUrl: (url) => {
        set((state) => {
          // Website on the backend can be plural, so starting to add
          //  functionality here, but currently only support 1 URL.
          state.websiteUrls = [url];
        });
      },

      updateWebsiteUrl: (index, url) => {
        set((state) => {
          // Website on the backend can be plural, so starting to add
          //  functionality here, but currently only support 1 URL.
          state.websiteUrls[index] = url;
        });
      },

      addTask: (task) => {
        set((state) => {
          // Check if task with same ID already exists
          const existingTaskIndex = state.activeTasks.findIndex(
            (t) => t.id === task.id
          );
          if (existingTaskIndex === -1) {
            // Only add if task doesn't exist
            state.activeTasks.push(task);
          } else {
            // Update existing task if status is different
            const existingTask = state.activeTasks[existingTaskIndex];
            if (existingTask.status !== task.status) {
              state.activeTasks[existingTaskIndex] = task;
            }
          }
        });
      },

      updateTaskStatus: (taskId, status) => {
        set((state) => {
          const task = state.activeTasks.find((t) => t.id === taskId);
          if (task) {
            // Prevent status downgrades
            const currentStatus = task.status;
            const statusOrder = ['NEW', 'SCRAPING', 'ERROR', 'DONE'];
            const currentIndex = statusOrder.indexOf(currentStatus);
            const newIndex = statusOrder.indexOf(status);

            // Only update if the new status is higher in the order or if it's an error
            if (newIndex > currentIndex || status === 'ERROR') {
              task.status = status;
            }
          }
        });
      },

      setFrontendFlowComplete: (isComplete) => {
        set((state) => {
          state.isFrontendFlowComplete = isComplete;
        });
      },

      setBackendFlowActive: (isActive) => {
        set((state) => {
          state.isBackendFlowActive = isActive;
        });
      },

      clearTasks: () => {
        set((state) => {
          state.activeTasks = [];
        });
      },

      addFeedMessage: async (feedMessage, timeout) => {
        await new Promise((resolve) => setTimeout(resolve, timeout || 0));
        const newFeedMessage = {
          ...feedMessage,
          roleUser: feedMessage.role === 'user',
        };
        set((state) => {
          state.feed = [...state.feed, newFeedMessage];
          state.lastFeedMessage = newFeedMessage;
        });
        return newFeedMessage;
      },

      patchFeedMessage: (id, update) => {
        const feed = get().feed;
        const messageIndex = feed.findIndex((m) => m.id === id);
        if (messageIndex >= 0) {
          const feedMessage = feed[messageIndex];
          set((state) => {
            state.feed[messageIndex] = { ...feedMessage, ...update };
          });
        }
      },

      setChatIsDone: (chatIsDone) => {
        set((state) => {
          state.chatIsDone = chatIsDone;
        });
      },

      setShowChatModal: (showChatModal) => {
        set((state) => {
          state.showChatModal = showChatModal;
        });
      },

      setStateId: (stateId) => {
        set((state) => {
          state.stateId = stateId;
        });
      },

      setModalFlow: (chatFlowId) => {
        set((state) => {
          state.chatFlowId = chatFlowId;
        });
      },

      setChatFlowStep: (chatFlowStep) => {
        set((state) => {
          state.chatFlowStep = chatFlowStep;
        });
      },

      setChatFlowLog: (chatFlowStepId, stepData) => {
        set((state) => {
          state.chatFlowLog = {
            ...state.chatFlowLog,
            [chatFlowStepId]: stepData,
          };
        });
      },

      setWaitingForAnswer: (waitingForAnswer) => {
        set((state) => {
          state.waitingForAnswer = waitingForAnswer;
        });
      },

      setWaitingForInky: (waitingForInky) => {
        set((state) => {
          state.waitingForInky = waitingForInky;
        });
      },

      setHasError: (hasError) => {
        set((state) => {
          state.hasError = hasError;
        });
      },

      setIsBrandStrategyCreating: (isCreating) => {
        set((state) => {
          state.isBrandStrategyCreating = isCreating;
        });
      },

      setBrandStrategyTaskIds: (taskIds) => {
        set((state) => {
          state.brandStrategyTaskIds = taskIds;
        });
      },
    })),
    {
      name: 'chat-storage',
      partialize: (state) => ({
        chatIsDone: state.chatIsDone,
        stateId: state.stateId,
        projectId: state.projectId,
      }),
    }
  )
);

export default useChatStore;
